import { Inject } from '@odinjs/odin';
import moment from 'moment';

import { AutocompleteAPI, BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, MapperAPI, PanelAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ClickEvent, FetchEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isFunction } from '@philips-internal/oswald-ui-utils';

import { EnteralNutritionStartAliases, FragmentNames } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ActionType, AdminType, InfusionHourStatus, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { ApplicationRoute } from '../../../../../constants/routes';
import { BarcodeUtils } from '../../../../../utils/barcode-utils';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import { YesNoType } from '../../../../../utils/enum-utils';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import AdminMedicationScheduleReadingCard from '../../../services/admin-medication-schedule-card-reading-service';
import { AdminModalsFractionalDoseService } from '../../../services/admin-modals-fractional-dose-service';
import { AdminModalsService } from '../../../services/admin-modals-service';
import MedicationService, { ConsistAdministrationMarginInfoResponse } from '../../../services/medication-service';
import { ConsistAdministrationMarginInfo } from '../../recommendation-orders/consist-administration-margin-info';
import { PrescriptionType } from '../../../services/timeline-service';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { AdminScheduleType } from '../../administration-schedule/administration-schedule-types';
import { AdminByProcessParams } from '../../medication-administration-by-process/medication-administration-by-process-params';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { EnteralNutritionType, GenerateChangePrescribedSolutionResponse, ResponseBodyProps } from '../enteral-nutrition-type';
import { StartEnteralNutritionForm, UnitMeasures, UnitMeasuresDose } from './enteral-nutrition-start-type';
import BarcodeService from '../../../../../services/barcode-service';
import GenericService from '../../../../../services/generic-service';


@Controller({ component: EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_LAYOUT, fragment: FragmentNames.ENTERAL_NUTRITION_START })
export default class EnteralNutritionStartController extends BaseController {

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_NAME_TEXT)
  private nameTextApi!: TextAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DATE_TIME_TEXT)
  private dateTimeTextApi!: TextAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_NEW_DATE_INPUT)
  private newDateInputApi!: InputAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BUTTON)
  private startButtonApi!: ButtonAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_MAPPER)
  private deviceMapperApi!: MapperAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_FIELD)
  private deviceFieldApi!: FieldAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_INFUSION_FORM_FIELD)
  private infusionFormFieldApi!: FieldAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_FORM)
  private formApi!: FormAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_UNITY_FIELD)
  private unityFieldApi!: FieldAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BARCODE_PANEL)
  private barcodePanelApi!: PanelAPI

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_CARDS_ACCORDION_PANEL)
  private cardsAccordionPanelApi!: PanelAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_CARD_DECK)
  private cardDeckApi!: DeckAPI;

  @Inject(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BARCODE)
  private barcodeApi!: BarcodeAPI;

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_FORM)
  handleMountForm(): void {
    InformationBarUtils.collapsedBar = true;

    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType };
    AdminByScheduleParams.readParam = { noCardsRead: true };
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);

    this.context.set({
      hasDevice: displayDevices === YesNoType.NO,
      materialCode: enteralNutrition.materialSequence,
    });
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_NEW_DATE_INPUT)
  handleMountNewDateInput(): void {
    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType }
    const newDate = moment().utc().format(ISO_FULL_DATE_FORMAT);

    this.nameTextApi.setValue(enteralNutrition.materialDescription);
    this.dateTimeTextApi.setValue(moment(enteralNutrition.scheduleDate).utc().format(ISO_FULL_DATE_FORMAT));
    this.newDateInputApi.setValue(newDate);
    this.context.set({ currentTime: newDate, dateTime: newDate });
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_INFUSION_FORM_AUTOCOMPLETE)
  handleMountInfusionFormAutocomplete(): void {
    this.fetchInfusionForm().then((response) => {
      const currentRecord = this.formApi.getRecord();
      this.formApi.setRecord({
        ...currentRecord,
        infusionType: {
          unitMeasure: response[0].unitMeasure,
          unitMeasureValue: response[0].unitMeasure
        }
      })
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  @OnMount([EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_FIELD, EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_INFUSION_FORM_FIELD, EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_TEXT_OBSERVATION_FIELD])
  handleMountDeviceField({ target }: EventData<FieldAPI>): void {
    const { itemStatus } = MedicineAndSolutionsParams.params;
    target.setVisible(itemStatus === InfusionItemStatus.NOT_STARTED)
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_AUTOCOMPLETE)
  handleMountDeviceAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    if (this.shouldDisplayDevices()) {
      const deviceDescription = 'deviceDescription';

      target.setEntry(deviceDescription);
      target.setPrimaryKey(deviceDescription);
    }
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_MAPPER)
  handleMountDeviceMapper(): void {
    if (this.shouldDisplayDevices()) {
      const deviceDescription = 'deviceDescription';
      this.deviceMapperApi.setMap({ value: deviceDescription });
    }
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_FETCH)
  handleMountDeviceFetch({ target }: EventData<FetchAPI>): void {
    if (this.shouldDisplayDevices()) {
      const { encounterId } = this.historyService.getCurrentRouteParams();
      this.context.set({ encounterCode: encounterId });
      target.setEndpoint(Endpoint.EMAR_FIND_INFUSIONS_DEVICE);
    }
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_VOLUME_STEP_FIELD)
  handleMountVolumeStepField({ target }: EventData<FieldAPI>): void {
    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType }
    const { doseQuantity, unitMeasureDoseDescription } = enteralNutrition
    target.setValue(doseQuantity ? `${doseQuantity} ${unitMeasureDoseDescription}` : '')
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_INFUSION_SPEED_FIELD)
  handleMountInfusionSpeed({ target }: EventData<FieldAPI>): void {
    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType }
    target.setValue(enteralNutrition.infusionSpeed ?? '')
  }

  @OnAfterFetch(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_UNITY_FETCH)
  handleAfterFetchUnityFetch(event: FetchEvent): void {
    const unityMeasure = event.data as UnitMeasuresDose[]
    this.unityFieldApi.setVisible(!!unityMeasure.length)
  }

  @OnAfterFetch(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_INFUSION_FORM_FETCH)
  handleAfterFetchInfusionFormFetch(event: FetchEvent): void {
    const infusionForm = event.data as UnitMeasures[]
    this.infusionFormFieldApi.setVisible(!!infusionForm.length)
  }

  @OnAfterFetch(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_DEVICE_FETCH)
  handleAfterFetchDeviceFetch(event: FetchEvent): void {
    const deviceFetchApi = event.target as unknown as FetchAPI;
    const devices = event.data as UnitMeasures[]

    deviceFetchApi.setParameters({ loading: false }, { skip: true });
    deviceFetchApi.clearPersistedState();

    this.deviceFieldApi.setVisible(!!devices.length)
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BUTTON)
  handleMountStartButton(): void {
    this.setStartButtonValue();
    this.validateIfBarcodeAndAccordionCardsIsVisible();
  }

  @OnClick(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BUTTON)
  async handleClickStartButton(): Promise<void> {
    const isValid = await this.formApi.validate();
    if (!isValid) return;

    const param712 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.VALIDATE_SOLUTION_MARGIN_ALL_STEPS);
    const isRestartSolution = this.context.get('actionType') === ActionType.RESTART_SOLUTION;
    const shouldValidateMargin = !isRestartSolution || param712 === 'S';

    if (!shouldValidateMargin) {
      this.fetchEnteralNutrition();
      return;
    }

    this.startButtonApi.setEnabled(false);

    try {
      const response = await this.consistAdministrationMarginInfoResponse();
      if (response.responseDescription) {
        MedicationService.showToast(response.responseDescription, ToastLevel.WARNING);
        this.startButtonApi.setEnabled(true);
        return;
      }
    } catch (error: any) {
      this.startButtonApi.setEnabled(true);
      if (error?.body?.errors) {
        error.body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
      } else if (error?.body?.message) {
        MedicationService.showToast(error.body.message, ToastLevel.ERROR);
      }
      return;
    }

    this.fetchEnteralNutrition();
  }

  @OnMount(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BARCODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_BARCODE)
  handleReadSuccessBarcode(event: ReadEvent<BarcodeData>): void {
    const { preventDefault } = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }

    AdminByScheduleParams.readParam = { noCardsRead: true };
    const eventValue = event.item.value.trim();

    this.readOneEnteralNutritionCard(eventValue).then((responseBody) => {
      const { informDose, totalRead, total, message, messageType } = responseBody as ResponseBodyProps;

      if (informDose) {
        this.handleModalAdminScheduleFractionalDose(eventValue);
        return;
      }

      if (!message) {
        MedicationService.showToast(MessageService.translate('text.medication-read-with-success'), ToastLevel.SUCCESS);
      } else {
        MedicationService.showToast(String(message), this.toastType(String(messageType)));
      }

      this.startButtonApi.setEnabled(false);
      this.barcodeApi.setEnabled(true);
      this.context.set({ allMedicationsRead: false });

      this.fetchItemsToCheck();

      if (Number(totalRead) > 0) {
        AdminByScheduleParams.readParam = { noCardsRead: false };
      }

      if (total !== 0 && totalRead === total) {
        this.startButtonApi.setEnabled(true);
        this.barcodeApi.setEnabled(false);
        this.barcodeApi.close();
        this.context.set({ allMedicationsRead: true });
        return;
      }
    });
  }

  @OnClick(EnteralNutritionStartAliases.ENTERAL_NUTRITION_START_CARD)
  handleClickCard(event: ClickEvent): void {
    const scheduleSequence = event.value.scheduleSequence;

    BarcodeUtils.dialogModalConfirm(scheduleSequence, this.resetOneMedicationsCard);
  }

  private fetchEnteralNutrition(): void {
    const body = this.getEnteralNutritionBody();

    this.startButtonApi.setEnabled(false);

    HttpService.post(Endpoint.EMAR_START_ENTERAL_NUTRITION, {}, { body })
      .then((response) => {
        const msg = (response?.body ?? response) as GenerateChangePrescribedSolutionResponse;
        if(msg.messageDescription){
          const messages = Array.isArray(msg.messageDescription) ? msg.messageDescription : [msg.messageDescription];
          const cleanMessage = GenericService.replaceMessage(messages);
          cleanMessage.forEach((message: string) => MedicationService.showToast(message, ToastLevel.WARNING));
          return;
        }
        this.showResponseMessage(response.body as string);
        this.redirectToTimeline();
      })
      .catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
      .finally(() => {
        this.startButtonApi.setEnabled(true);
      })

  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    const actionType = this.context.get('actionType')
    switch (actionType) {
      case ActionType.START_SOLUTION:
        MedicationService.showToast(MessageService.translate('text.infusion-started-with-success'), ToastLevel.SUCCESS);
        break;
      case ActionType.RESTART_SOLUTION:
        MedicationService.showToast(MessageService.translate('text.infusion-restarted-with-success'), ToastLevel.SUCCESS);
        break;
      default:
        break;
    }
  }

  private getEnteralNutritionBody(): Record<string, Value> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { currentTime, enteralNutrition } = this.context.getBulkData() as { currentTime: string, enteralNutrition: EnteralNutritionType }
    const formRecord = this.formApi.getRecord() as unknown as StartEnteralNutritionForm;
    const notShouldDisplayDevices = !this.shouldDisplayDevices();
    const unitMeasureValue = formRecord.infusionType?.unitMeasureValue;
    const infusionType = this.getInfusionType(unitMeasureValue)
    const measureUnitCode = formRecord.unityMeasure?.measurementUnitCode;
    const dosageType = this.getDosageType(measureUnitCode)
    const formatedInfusionSpeed = String(formRecord.infusionSpeed).replace(',', '.');
    const actionType = this.context.get('actionType')

    const body = {
      actionType,
      changeDate: currentTime,
      dateTime: enteralNutrition.scheduleDate,
      deviceSequence: notShouldDisplayDevices ? null : formRecord.pumpType?.deviceCode,
      dosageType,
      encounterNumber: encounterId,
      infusionPump: notShouldDisplayDevices ? formRecord.device?.unitMeasureValue : null,
      infusionSpeedQuantity: Number(formatedInfusionSpeed),
      infusionType,
      itemCode: Number(enteralNutrition.materialCode),
      justifyDescription: '',
      observationDescription: formRecord.observation ?? '',
      pdoPrnStatus: BooleanUtils.isTrueValue(enteralNutrition.isPdo),
      prescriptionNumber: Number(enteralNutrition.prescriptionNumber),
      scheduleSequenceNumber: Number(enteralNutrition.scheduleSequence),
      solutionSequence: enteralNutrition.materialSequence,
      volumePhaseQuantity: Number(String(formRecord.volumePhase).split(' ')[0])
    } as Record<string, Value>;

    return body;
  }

  private fetchInfusionForm(): Promise<ResponseBodyProps[]> {
    const infusionFormDomainSequence = 1883;
    const activeSituation = 'A';

    return HttpService.get(Endpoint.EMAR_FIND_INFUSIONS_FORM, { domain: infusionFormDomainSequence, situation: activeSituation })
      .then(res => res.body as ResponseBodyProps[])
  }

  fetchItemsToCheck(): Promise<Record<string, Value>[]> {
    const { nrPrescriptionId, id, startId } = MedicineAndSolutionsParams.params

    const params = {
      prescriptionNumber: nrPrescriptionId,
      prescriptionType: PrescriptionType.ENTERAL_NUTRITION.valueOf(),
      scheduleDate: startId,
      scheduleSequence: id,
    }

    return HttpService.get(Endpoint.EMAR_GET_MEDICATIONS_PENDING_PRESCRIPTION_TYPE, params).then((response) => {
      const pendingItems = response.body as Record<string, Value>[];

      const hasNoReading = pendingItems.filter(((item) => item.read === false)).length === 0;
      const hasReading = pendingItems.filter(((item) => item.read === true)).length > 0;

      this.cardDeckApi?.setDone(true);
      this.cardDeckApi?.setLoading(true);

      if (hasReading) {
        AdminByScheduleParams.readParam = { noCardsRead: false };
      }

      if (hasNoReading) {
        this.startButtonApi.setEnabled(true);
        this.barcodeApi.setEnabled(false);
        this.context.set({ allMedicationsRead: true });
      }

      if (pendingItems?.length > 0) {
        this.cardDeckApi?.setData(pendingItems);
        this.cardDeckApi?.setLoading(false);
      }

      return pendingItems;
    })
  }

  private readOneEnteralNutritionCard(barcodeRead: string): Promise<void | Record<string, Value>> {
    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType }
    const requestBody = {
      barcode: barcodeRead,
      prescriptionNumber: enteralNutrition.prescriptionNumber,
      prescriptionType: PrescriptionType.ENTERAL_NUTRITION.valueOf(),
      scheduleDate: enteralNutrition.scheduleDate,
      scheduleSequence: enteralNutrition.scheduleSequence,
    }

    return AdminMedicationScheduleReadingCard.readOneMedicationCard(requestBody)
  }

  private toastType(message: string): ToastLevel {
    return message.toLowerCase() === ToastLevel.ERROR ? ToastLevel.ERROR : ToastLevel.SUCCESS
  }

  private getDosageType(measureUnitCode: string): string {
    const validMeasureUnit = [AdminType.GRAMA.valueOf(), AdminType.MILIGRAMAS.valueOf(), AdminType.MILILITROS.valueOf()]
    return !validMeasureUnit.includes(measureUnitCode) ? AdminType.MILILITROS_PER_HOUR : measureUnitCode;
  }

  private getInfusionType(unitMeasureValue?: string): AdminType {
    return unitMeasureValue !== AdminType.OPEN && unitMeasureValue !== AdminType.TIME &&
      unitMeasureValue !== AdminType.INFUSION_SPEED ? AdminType.INFUSION_SPEED : unitMeasureValue;
  }

  private shouldDisplayDevices(): boolean {
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
    return displayDevices === YesNoType.YES;
  }

  private setStartButtonValue(): void {
    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType }
    const { itemStatus, ieStatus } = MedicineAndSolutionsParams.params;
    this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));
    const solutionItemStatusInterrupted = itemStatus === InfusionItemStatus.II || itemStatus === InfusionItemStatus.INTERRUPTED;

    if (ieStatus === InfusionHourStatus.INTERRUPTED && solutionItemStatusInterrupted) {
      this.context.set({ actionType: ActionType.RESTART_SOLUTION })
      this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.restart-solution'));
      return;
    }

    if (enteralNutrition.statusItem !== InfusionHourStatus.STARTED) {
      this.context.set({ actionType: ActionType.START_SOLUTION });
      this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));
      return;
    }
  }

  private validateIfBarcodeAndAccordionCardsIsVisible(): void {
    const checkMedicationBeforeAdmin = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const isAdmProcess = AdminByProcessParams.admType.isAdmProcess;
    const isRestartSolution = this.context.get('actionType') === ActionType.RESTART_SOLUTION;

    if (isAdmProcess || checkMedicationBeforeAdmin === YesNoType.NO || isRestartSolution) {
      this.barcodePanelApi.setVisible(false);
      this.cardsAccordionPanelApi.setVisible(false);
      this.startButtonApi.setEnabled(true);
      return
    }

    this.resetAllItems().then(() => this.fetchItemsToCheck())
    this.startButtonApi.setEnabled(false);
  }

  resetOneMedicationsCard = (barcodeValue: string): void => {
    const { id, nrPrescriptionId, startId } = MedicineAndSolutionsParams.params;

    HttpService.put(Endpoint.EMAR_RESET_ONE_MEDICATIONS_CARD, {
      prescriptionNumber: nrPrescriptionId,
      scheduleCurrentSequence: Number(barcodeValue),
      scheduleDate: startId,
      scheduleSequence: id,
    }).then(({ body }) => {
      const { message } = body as Record<string, Value>[] as ResponseBodyProps;

      MedicationService.showToast(String(message), ToastLevel.SUCCESS);

      this.fetchItemsToCheck().then((responseBody) => {
        const responseSchedule = responseBody as Record<string, Value>[] as ResponseBodyProps[];
        const totalRead = responseSchedule.filter(((item) => item.read === true));
        const withoutReading = responseSchedule.filter(((item) => item.read === false));

        if (withoutReading.length > 0) {
          this.barcodeApi.setEnabled(true);
          this.startButtonApi.setEnabled(false);
          this.context.set({ readPendding: false });
        }

        this.cardDeckApi.setData(responseBody);
        AdminByScheduleParams.readParam = { noCardsRead: true };

        if (totalRead.length > 0) {
          AdminByScheduleParams.readParam = { noCardsRead: false };
        }
      }).catch((error) => {
        MedicationService.showToast(error.message, ToastLevel.ERROR);
      });
    })
  }

  private resetAllItems(): Promise<void | Record<string, Value>[]> {
    const { id, nrPrescriptionId, startId } = MedicineAndSolutionsParams.params;

    return HttpService.put(Endpoint.EMAR_RESET_ALL_MEDICATIONS_CARD, {
      prescriptionNumber: nrPrescriptionId,
      scheduleDate: startId,
      scheduleSequence: id,
    }).then(({ body }) => body as Record<string, Value>[])
      .catch((error) => {
        MedicationService.showToast(error.message, ToastLevel.ERROR);
      });
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.ENTERAL_NUTRITION_START);
    AdminModalsService.redirectToTimeline(FragmentNames.ENTERAL_NUTRITION_VIEWER);
  }

  handleBarcodeClose(): void {
    this.barcodeApi?.close();
  }

  private handleModalAdminScheduleFractionalDose(currentBarcode: string): void {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const { nrPrescriptionId, startId, unitMeasureDoseDescription } = MedicineAndSolutionsParams.params;

    const paramsAdmFractionDose = {
      barcodeClose: () => this.handleBarcodeClose(),
      currentBarcode,
      medicationOrSolution: AdminScheduleType.MEDICATION,
      prescriptionNumber: Number(nrPrescriptionId),
      prescriptionType: PrescriptionType.ENTERAL_NUTRITION,
      scheduleDate: String(startId),
      scheduleSequenceNumber: Number(scheduleSequenceNumber),
      unitMeasureDoseDescription,
      updatedCards: () => this.fetchItemsToCheck()
    }

    AdminModalsFractionalDoseService.modalAdminScheduleFractionalDose(paramsAdmFractionDose);
  }

  private consistAdministrationMarginInfoResponse(): Promise<ConsistAdministrationMarginInfoResponse> {
    const { enteralNutrition } = this.context.getBulkData() as { currentTime: string, enteralNutrition: EnteralNutritionType };
    const formRecord = this.formApi.getRecord() as unknown as StartEnteralNutritionForm;
    const params = {
      administrationDate: formRecord.currentTime,
      itemType: PrescriptionType.ENTERAL_NUTRITION,
      justification: formRecord.justification,
      scheduleSequenceNumber: enteralNutrition.scheduleSequence
    };

    return ConsistAdministrationMarginInfo.execute(params);
  }
}
